/**
 * The column state of one ClimGrid: the widths (drag and arrow keys), which columns are shown, and the sort, kept
 * together in ONE localStorage entry per grid (`clim-grid:<gridId>`). Masterplan credit-limits-v3, package A4, E14;
 * "Nicht in diesem Plan": widths, visibility and sort stay in the browser, the app has no preference endpoint.
 *
 * Copied from inventory-app `web/src/components/table/useResizableColumns.ts` (read 2026-10-22; itself after sales-app
 * and this repo's `hooks/useResizableColumns.ts`), with two changes: the store is localStorage instead of inventory's
 * UserPrefs provider, and the entry also holds the hidden columns and the sort. Kept from the source, because a
 * re-implementation always misses them:
 *
 *   1. `shouldSuppressClick()`: a drag ends with a mouseup inside the header cell, which the browser reports as a
 *      click; without swallowing exactly that one click every resize also toggles the sort.
 *   2. The drag listeners live on `window`, so the pointer may leave the header mid-drag; the effect tears them down.
 *   3. `nudge`: ArrowLeft/ArrowRight on the handle, so a column is resizable without a mouse.
 *   4. While a drag runs the width lives in this hook; the mouseup writes it once (not forty writes per drag).
 *
 * Anything stored that is unparseable, unknown or out of range is dropped, never clamped into place: a stored width
 * is a convenience, and a column silently 3 px wide is worse than one at its default.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const MIN_COL_WIDTH = 60;
export const MAX_COL_WIDTH = 900;
export const STORAGE_PREFIX = 'clim-grid:';
/** Bumped when the stored shape changes; an entry of another version is ignored. */
export const PREFS_VERSION = 1;

export type SortDirection = 'asc' | 'desc';
export interface ClimSort { key: string; direction: SortDirection }

/** What the hook needs of a column; ClimGrid's column definition is richer. */
export interface ClimColumnSpec {
    key: string;
    /** Default width in px, used until the person drags this column. */
    width: number;
    /** false: always shown, not offered in the column picker (the key column). Default true. */
    hideable?: boolean;
    /** false: the header is plain text. Default true. */
    sortable?: boolean;
    /** Hidden until the person shows it. Default false. */
    hiddenByDefault?: boolean;
}

export interface ClimColumnPrefs {
    widths: Record<string, number>;
    /** Keys the person hid, or showed although hidden by default, relative to the defaults: see `hiddenSet`. */
    hidden: string[];
    shown: string[];
    sort: ClimSort | null;
}

export const EMPTY_PREFS: ClimColumnPrefs = { widths: {}, hidden: [], shown: [], sort: null };

export function storageKeyOf(gridId: string): string {
    return `${STORAGE_PREFIX}${gridId}`;
}

/** A width within the limits, whole pixels. */
export function clampWidth(px: number, min = MIN_COL_WIDTH, max = MAX_COL_WIDTH): number {
    return Math.min(max, Math.max(min, Math.round(px)));
}

/**
 * The stored entry, validated against the grid's columns. Pure, so the rules are tested without a browser.
 * Unknown keys, widths out of range, a hidden key that cannot be hidden and a sort on a column that cannot sort are
 * dropped one by one; the rest of the entry survives.
 */
export function parseColumnPrefs(raw: string | null, columns: readonly ClimColumnSpec[], min = MIN_COL_WIDTH, max = MAX_COL_WIDTH): ClimColumnPrefs {
    if (!raw) return EMPTY_PREFS;
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { return EMPTY_PREFS; }
    if (!parsed || typeof parsed !== 'object') return EMPTY_PREFS;
    const p = parsed as Record<string, unknown>;
    if (p.v !== PREFS_VERSION) return EMPTY_PREFS;

    const known = new Set(columns.map(c => c.key));
    const hideable = new Set(columns.filter(c => c.hideable !== false).map(c => c.key));
    const sortable = new Set(columns.filter(c => c.sortable !== false).map(c => c.key));

    const widths: Record<string, number> = {};
    if (p.widths && typeof p.widths === 'object') {
        for (const [k, v] of Object.entries(p.widths as Record<string, unknown>)) {
            if (known.has(k) && typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max) widths[k] = Math.round(v);
        }
    }
    const keys = (v: unknown): string[] => (Array.isArray(v) ? [...new Set(v.filter((k): k is string => typeof k === 'string' && hideable.has(k)))] : []);
    const hidden = keys(p.hidden);
    const shown = keys(p.shown).filter(k => !hidden.includes(k));

    let sort: ClimSort | null = null;
    const s = p.sort as Record<string, unknown> | null | undefined;
    if (s && typeof s === 'object' && typeof s.key === 'string' && sortable.has(s.key) && (s.direction === 'asc' || s.direction === 'desc')) {
        sort = { key: s.key, direction: s.direction };
    }
    return { widths, hidden, shown, sort };
}

export function serializeColumnPrefs(prefs: ClimColumnPrefs): string {
    return JSON.stringify({ v: PREFS_VERSION, widths: prefs.widths, hidden: prefs.hidden, shown: prefs.shown, sort: prefs.sort });
}

/** The keys not shown: hidden by default unless the person showed them, plus the ones the person hid. */
export function hiddenSet(columns: readonly ClimColumnSpec[], prefs: ClimColumnPrefs): Set<string> {
    const out = new Set<string>();
    for (const c of columns) {
        if (c.hideable === false) continue;
        if (prefs.hidden.includes(c.key)) out.add(c.key);
        else if (c.hiddenByDefault && !prefs.shown.includes(c.key)) out.add(c.key);
    }
    return out;
}

function readStorage(key: string): string | null {
    try { return typeof window === 'undefined' ? null : window.localStorage.getItem(key); } catch { return null; }
}

function writeStorage(key: string, value: string): void {
    try { if (typeof window !== 'undefined') window.localStorage.setItem(key, value); } catch { /* quota or blocked */ }
}

/**
 * @param columns ALL columns of the grid, hidden ones included: validation drops unknown keys, so passing only the
 *                visible subset would delete the width of a hidden column the next time the entry is written.
 * @param gridId  unique per grid, e.g. `overview`; the entry is `clim-grid:<gridId>`.
 * @param defaultSort the sort until the person picks one.
 */
export function useClimColumns(columns: readonly ClimColumnSpec[], gridId: string, defaultSort: ClimSort | null = null) {
    const storageKey = storageKeyOf(gridId);
    const [prefs, setPrefs] = useState<ClimColumnPrefs>(() => parseColumnPrefs(readStorage(storageKey), columns));
    const [live, setLive] = useState<{ key: string; width: number } | null>(null);
    const [resizing, setResizing] = useState<{ key: string; startX: number; startWidth: number } | null>(null);
    const suppressClick = useRef(false);
    const liveRef = useRef(live);
    liveRef.current = live;
    const firstWrite = useRef(true);

    useEffect(() => {
        // The first render only read the entry; writing it straight back would turn a stored-but-invalid entry
        // into a cleaned one before the person did anything. Every later change is written.
        if (firstWrite.current) { firstWrite.current = false; return; }
        writeStorage(storageKey, serializeColumnPrefs(prefs));
    }, [prefs, storageKey]);

    const defaults = useMemo(() => Object.fromEntries(columns.map(c => [c.key, c.width])) as Record<string, number>, [columns]);

    const widthOf = useCallback(
        (key: string) => (live && live.key === key ? live.width : prefs.widths[key] ?? defaults[key] ?? MIN_COL_WIDTH),
        [live, prefs.widths, defaults],
    );
    const totalWidthOf = useCallback((keys: readonly string[]) => keys.reduce((sum, k) => sum + widthOf(k), 0), [widthOf]);

    const writeWidth = useCallback((key: string, width: number | null) => {
        setPrefs(p => {
            const widths = { ...p.widths };
            if (width === null) delete widths[key];
            else widths[key] = clampWidth(width);
            return { ...p, widths };
        });
    }, []);

    useEffect(() => {
        if (!resizing) return;
        const onMove = (ev: globalThis.MouseEvent) => {
            setLive({ key: resizing.key, width: clampWidth(resizing.startWidth + ev.clientX - resizing.startX) });
        };
        const onUp = () => {
            suppressClick.current = true;
            // Clears itself in case the mouse came up outside the cell and no click ever arrives.
            window.setTimeout(() => { suppressClick.current = false; }, 200);
            const done = liveRef.current;
            if (done && done.key === resizing.key) writeWidth(done.key, done.width);
            setLive(null);
            setResizing(null);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        const prev = { cursor: document.body.style.cursor, select: document.body.style.userSelect };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            document.body.style.cursor = prev.cursor;
            document.body.style.userSelect = prev.select;
        };
    }, [resizing, writeWidth]);

    const startResize = useCallback((key: string, clientX: number) => {
        setResizing({ key, startX: clientX, startWidth: widthOf(key) });
    }, [widthOf]);

    /** Back to the column's default width. */
    const resetWidth = useCallback((key: string) => writeWidth(key, null), [writeWidth]);

    /** Keyboard resizing: ±`step` px, clamped like a drag. */
    const nudge = useCallback((key: string, step: number) => writeWidth(key, widthOf(key) + step), [writeWidth, widthOf]);

    /** Call at the top of the header's onClick: `true` means «this click is the tail of a drag, ignore it». */
    const shouldSuppressClick = useCallback(() => {
        if (!suppressClick.current) return false;
        suppressClick.current = false;
        return true;
    }, []);

    const hidden = useMemo(() => hiddenSet(columns, prefs), [columns, prefs]);

    const setVisible = useCallback((key: string, visible: boolean) => {
        const col = columns.find(c => c.key === key);
        if (!col || col.hideable === false) return;
        setPrefs(p => {
            const hiddenKeys = p.hidden.filter(k => k !== key);
            const shownKeys = p.shown.filter(k => k !== key);
            if (!visible && !col.hiddenByDefault) hiddenKeys.push(key);
            if (visible && col.hiddenByDefault) shownKeys.push(key);
            return { ...p, hidden: hiddenKeys, shown: shownKeys };
        });
    }, [columns]);

    /** Every column back to its default visibility. Widths and sort stay. */
    const resetVisibility = useCallback(() => setPrefs(p => ({ ...p, hidden: [], shown: [] })), []);

    const sort = prefs.sort ?? defaultSort;
    const setSort = useCallback((next: ClimSort | null) => setPrefs(p => ({ ...p, sort: next })), []);

    return {
        widthOf, totalWidthOf, startResize, resetWidth, nudge, shouldSuppressClick,
        resizingKey: resizing?.key ?? null,
        hidden, setVisible, resetVisibility,
        sort, setSort,
    };
}
